













































































































































































import { Component, Vue, Watch } from 'vue-property-decorator';
import { AlbumUploadService } from '@/api';
import { APIError } from '@/api/api';
import { AlbumUpload, TrackUploadStage } from '@/types/albumUpload';
import UBlock from '@/ui-lib/block/Block.vue';
import UButton from '@/ui-lib/button/Button.vue';
import UAlert from '@/ui-lib/alert/Alert.vue';

const POLL_INTERVAL = 1000;

const STAGE_LABELS: Record<string, string> = {
  transcoding: 'Кодируется',
  waveform: 'Строим волну',
  uploading: 'Выгружается',
  embedding: 'Определяем вектор'
};

@Component<AlbumUploadProgressView>({
  components: {
    UAlert,
    UBlock,
    UButton
  },

  metaInfo() {
    return {
      title: 'Загрузка альбома'
    };
  }
})
export default class AlbumUploadProgressView extends Vue {
  public upload: AlbumUpload | null = null;
  public isLoading = true;
  public notFound = false;
  public loadError = false;
  public isActing = false;
  public actionError = '';

  private pollTimer: number | null = null;
  private isDestroyed = false;

  get id(): number {
    return Number(this.$route.params.id);
  }

  get doneCount(): number {
    if (!this.upload) {
      return 0;
    }
    return this.upload.tracks.filter(t => t.status === 'done').length;
  }

  public stageLabel(stage: TrackUploadStage | null): string {
    if (!stage) {
      return 'Обрабатывается';
    }
    return STAGE_LABELS[stage] || 'Обрабатывается';
  }

  mounted() {
    this.reload();
  }

  beforeDestroy() {
    this.isDestroyed = true;
    this.clearTimer();
  }

  // <router-view> переиспользует инстанс при смене :id — перезагружаем вручную.
  @Watch('$route.params.id')
  private onIdChange() {
    this.clearTimer();
    this.upload = null;
    this.notFound = false;
    this.loadError = false;
    this.isLoading = true;
    this.reload();
  }

  // Один опрос + (если обработка идёт) планирование следующего рекурсивным
  // setTimeout после ответа — запросы не накладываются.
  private async reload() {
    this.clearTimer();
    const requestedId = this.id;
    await this.fetchOnce(requestedId);

    // id мог смениться за время запроса (переключение в доке) — не планируем
    // тик для устаревшей загрузки.
    if (this.isDestroyed || requestedId !== this.id) {
      return;
    }

    if (this.upload && this.upload.status === 'processing') {
      this.pollTimer = window.setTimeout(() => this.reload(), POLL_INTERVAL);
    }
  }

  private async fetchOnce(requestedId: number) {
    try {
      const upload = await AlbumUploadService.getOne(requestedId);
      // Ответ устаревшего id — игнорируем, иначе перезатрём текущий альбом.
      if (requestedId !== this.id) {
        return;
      }
      this.upload = upload;
      this.notFound = false;
      this.loadError = false;
    } catch (e) {
      if (requestedId !== this.id) {
        return;
      }
      const err = e as APIError;
      if (err?.statusCode === 404) {
        // Загрузку удалили (TTL/админ/другая вкладка) — прекращаем поллинг
        // и показываем «не найдена».
        this.notFound = true;
        this.upload = null;
      } else if (!this.upload) {
        // Ошибка на первой загрузке (сервер лежит, обрыв) — показываем экран
        // ошибки. При активном поллинге уже загруженной просто держим стейт.
        this.loadError = true;
      }
    } finally {
      if (requestedId === this.id) {
        this.isLoading = false;
      }
    }
  }

  private clearTimer() {
    if (this.pollTimer !== null) {
      window.clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  public async retry() {
    this.isActing = true;
    this.actionError = '';
    try {
      await AlbumUploadService.retry(this.id);
      await this.reload();
    } catch (e) {
      const err = e as APIError;
      this.actionError = err?.message || 'Не удалось перезапустить.';
    }
    this.isActing = false;
  }

  public async remove() {
    this.isActing = true;
    this.actionError = '';
    try {
      await AlbumUploadService.remove(this.id);
      this.goToForm();
    } catch (e) {
      const err = e as APIError;
      this.actionError = err?.message || 'Не удалось удалить.';
      this.isActing = false;
    }
  }

  public goToForm() {
    this.$router.push({ name: 'albumUploadForm' });
  }

  public goToCatalog() {
    this.$router.push({ name: 'trackList' });
  }
}
